import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { Observable } from 'rxjs';

import {
    DcSensorValueProperties,
    DemandRelayModeProperties,
    DeviceConnectionStatus,
    DeviceConnectionStatusValues,
    DeviceFirmwareVersion,
    DeviceHeatingCircuitIdCommands,
    DeviceIds,
    DeviceNameCommands,
    DeviceNameProperties,
    Feature,
    FeatureCommands,
    FeatureNames,
    FeaturePropertyKey,
    FhtDemandRelayModeCommand,
    FhtDemandRelayModeValues,
    FloorHeatingDamageProtectionThresholdCommands,
    GatewayDevicesProperties,
    IsActiveProperty,
    IsNumberValueProperty,
    IsStringValueProperty,
    SignalProperties
} from '@vi-iot/client';

import { lowLqiSignalThreshold } from '@common';
import { NO_VALUE } from '@pipes';
import { GTMService } from '@services';
import {
    ActiveErrorsStore,
    GatewayDevice,
    GatewayDeviceGroup,
    InstallationFeaturesStore,
    isRemoteDeviceOfType,
    RemoteDeviceType
} from '@store';

import { InstallationService } from '../../services/installation.service';
import { EditDeviceNameModalComponent } from '../edit-device-name-modal/edit-device-name-modal.component';
import { FhtManagerModalComponent } from '../managers/fht-manager-modal/fht-manager-modal.component';

@Component({
  selector: 'app-other-devices-panel',
  templateUrl: './other-devices-panel.component.html',
  styleUrls: ['./other-devices-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OtherDevicesPanelComponent implements OnInit {
  readonly featurePropertyKey = FeaturePropertyKey;
  readonly srcType = RemoteDeviceType;
  readonly connectionStatus = DeviceConnectionStatusValues;
  readonly noValue = NO_VALUE;
  readonly FeatureCommands = FeatureCommands;
  readonly lowLqiSignalThreshold = lowLqiSignalThreshold;
  readonly fhtDemandRelayModeValues = FhtDemandRelayModeValues;

  isSrcStandalone$: Observable<Feature<IsActiveProperty, never>>;
  firmware$: Observable<Feature<DeviceFirmwareVersion, never>>;
  isRemoteDeviceOfType = isRemoteDeviceOfType;
  editDeviceNameDialogRef: MatDialogRef<EditDeviceNameModalComponent>;

  @Input() groupOfDevices: GatewayDeviceGroup[];

  constructor(
    public installationService: InstallationService,
    public activeErrorsStore: ActiveErrorsStore,
    private gtmService: GTMService,
    private dialog: MatDialog,
    private installationFeaturesStore: InstallationFeaturesStore
  ) {}

  ngOnInit(): void {
    this.isSrcStandalone$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.standaloneMode,
      DeviceIds.RoomControl
    );
    this.firmware$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.essFirmwareBundle,
    );
  }

  getDeviceNameFeature(
    deviceId: string
  ): Observable<Feature<DeviceNameProperties, DeviceNameCommands>> {
    return this.installationFeaturesStore.selectInstallationDeviceFeature<
      DeviceNameProperties,
      DeviceNameCommands
    >(FeatureNames.deviceName, deviceId);
  }

  openEditDeviceNameDialog(deviceId: string): void {
    this.installationService.selectedFhtDeviceId$.next(deviceId);
    this.gtmService.track('onOpen', 'Edit Device Name modal', 'otherRoomControl');
    this.editDeviceNameDialogRef = this.dialog.open(EditDeviceNameModalComponent, {
      width: '715px',
      panelClass: 'commissioning-edit-room-dialog',
      data: {
        deviceId,
      },
    });
  }

  // TODO commented by VIG-2626, VIG-2729
  //
  // getWifiFeature(deviceId: string): Observable<Feature> {
  //   return this.installationFeaturesStore.selectInstallationDeviceFeature(FeatureNames.deviceZigbee, deviceId);
  // }

  getWifiLqiFeature(deviceId: string): Observable<Feature<SignalProperties, never>> {
    return this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.deviceZigbeeLqi,
      deviceId
    );
  }

  getHeatingIdFeature(
    deviceId: string
  ): Observable<Feature<IsNumberValueProperty, DeviceHeatingCircuitIdCommands>> {
    return this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.deviceHeatingCircuitId,
      deviceId
    );
  }

  getFHTDemandRelayModeFeature(
    deviceId: string
  ): Observable<Feature<DemandRelayModeProperties, FhtDemandRelayModeCommand>> {
    return this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.fhtDemandRelayMode,
      deviceId
    );
  }

  onFHTDemandRelayModeChange(event: MatSlideToggleChange, deviceId: string): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.fhtDemandRelayMode,
      event.checked ? FhtDemandRelayModeValues.automatic : FhtDemandRelayModeValues.off,
      this.featurePropertyKey.value,
      null,
      FeatureCommands.setValue,
      null,
      deviceId
    );
  }

  getActiveOperatingModeFeature(
    deviceId: string
  ): Observable<Feature<IsStringValueProperty, never>> {
    return this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.fhtActiveOperatingMode,
      deviceId
    );
  }

  getFHTChannelsFeature(): Observable<Feature<GatewayDevicesProperties, never>> {
    return this.installationFeaturesStore.selectInstallationGatewayFeature(
      FeatureNames.gatewayDevices
    );
  }

  getFHTConnectionStatusFeature(
    deviceId: string
  ): Observable<Feature<DeviceConnectionStatus, never>> {
    return this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.deviceConnection,
      deviceId
    );
  }

  getFHTSensorsTemperatureSupplyFeature(
    deviceId: string
  ): Observable<Feature<DcSensorValueProperties, never>> {
    return this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.fhtSensorsTemperatureSupply,
      deviceId
    );
  }

  getFHTDamageProtectionThresholdFeature(
    deviceId: string
  ): Observable<Feature<IsNumberValueProperty, FloorHeatingDamageProtectionThresholdCommands>> {
    return this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.fhtDamageProtectionThreshold,
      deviceId
    );
  }

  setThreshold(deviceId: string): void {
    this.installationService.selectedFhtDeviceId$.next(deviceId);
  }

  getFHTChannelsId(devices: GatewayDevice[], fhtMainDeviceId: string): string[] {
    if (!devices || !devices.length) {
      return [];
    }

    fhtMainDeviceId = fhtMainDeviceId.replace('zigbee-', '').toLowerCase();

    return devices
      .filter((device: GatewayDevice) =>
        isRemoteDeviceOfType(device.roles, RemoteDeviceType.FHT_CHANNEL)
      )
      .map((device: GatewayDevice) => device.id.replace('zigbee-', '').toLowerCase())
      .filter((deviceId: string) => deviceId.slice(0, -2).toLowerCase() === fhtMainDeviceId);
  }

  openFhtModal(id: string): void {
    this.gtmService.track('onOpen', 'FHT modal', 'singleRoomControl');
    this.installationService.selectedFhtDeviceId$.next(id);

    this.dialog.open(FhtManagerModalComponent, {
      width: '880px',
      panelClass: 'app-fht-manager-dialog',
      disableClose: false,
    });
  }
}
